import { Component, Input, OnDestroy } from '@angular/core'
import { MatCheckboxChange } from '@angular/material/checkbox'
import { EligibilityBenefits, Estimate, EstimateInputs, EstimateOutput, EstimateService, PatientInsurance, PatientInsuranceStatus } from '@mmx/shared'
import { Subject } from 'rxjs'

export enum PrimaryBenefitField {
  Deductible = 'deductible',
  Copayment = 'copayment',
  Coinsurance = 'coinsurance',
  OutOfPocketMax = 'outOfPocketMax',
}

export enum SecondaryBenefitField {
  Deductible = 'secondaryDeductible',
  Copayment = 'secondaryCopayment',
  Coinsurance = 'secondaryCoinsurance',
  OutOfPocketMax = 'secondaryOutOfPocketMax',
}

type BenefitField = PrimaryBenefitField | SecondaryBenefitField

@Component({
  selector: 'mmx-estimate-benefits',
  templateUrl: './estimate-benefits.component.html',
  styleUrls: ['./estimate-benefits.component.scss'],
  standalone: false,
})
export class EstimateBenefitsComponent implements OnDestroy {
  @Input() shopper = false

  readonly PrimaryBenefitField = PrimaryBenefitField
  readonly SecondaryBenefitField = SecondaryBenefitField
  readonly PatientInsuranceStatus = PatientInsuranceStatus
  readonly draftStatus = Estimate.STATUS.DRAFT
  readonly estimate$ = this.estimateService.estimate$

  get inputs(): EstimateInputs {
    return this.estimateService.estimate$.getValue()?.inputs as EstimateInputs
  }

  get output(): EstimateOutput | undefined {
    return this.estimateService.estimate$.getValue()?.output
  }

  primaryInsurances$ = this.estimateService.primaryInsurances$
  primaryInsurance$ = this.estimateService.primaryInsurance$
  secondaryInsurances$ = this.estimateService.secondaryInsurances$
  secondaryInsurance$ = this.estimateService.secondaryInsurance$
  benefitsLoading$ = this.estimateService.benefitsLoading$
  hasSecondaryInsurance$ = this.estimateService.hasSecondaryInsurance$
  private onDestroy$ = new Subject<void>()

  constructor(
    private estimateService: EstimateService,
  ) {}

  get originalBenefits(): EligibilityBenefits | null {
    return this.estimateService.originalBenefits$.getValue()
  }

  get originalSecondaryBenefits(): EligibilityBenefits | null {
    return this.estimateService.originalSecondaryBenefits$.getValue()
  }

  private isSecondaryField(field: BenefitField): field is SecondaryBenefitField {
    return (Object.values(SecondaryBenefitField) as string[]).includes(field)
  }

  private toPrimaryKey(field: BenefitField): PrimaryBenefitField {
    switch (field) {
      case SecondaryBenefitField.Deductible:
        return PrimaryBenefitField.Deductible
      case SecondaryBenefitField.Copayment:
        return PrimaryBenefitField.Copayment
      case SecondaryBenefitField.Coinsurance:
        return PrimaryBenefitField.Coinsurance
      case SecondaryBenefitField.OutOfPocketMax:
        return PrimaryBenefitField.OutOfPocketMax
      default:
        return field
    }
  }

  isAmbiguous(field: BenefitField): boolean {
    const source = this.isSecondaryField(field) ? this.originalSecondaryBenefits : this.originalBenefits
    if (!source) return false

    const key = this.toPrimaryKey(field)
    if (key === PrimaryBenefitField.Copayment) return !!source.copaymentAmbiguous
    if (key === PrimaryBenefitField.Coinsurance) return !!source.coinsuranceAmbiguous
    return false
  }

  isModified(field: BenefitField): boolean {
    if (!this.inputs) return false

    const source = this.isSecondaryField(field) ? this.originalSecondaryBenefits : this.originalBenefits
    if (!source) return false

    const key = this.toPrimaryKey(field)
    const defaultValue = key === PrimaryBenefitField.OutOfPocketMax ? null : 0
    const originalValue = source[key] ?? defaultValue

    return (this.inputs[field] ?? defaultValue) !== originalValue
  }

  get coinsurancePercent(): number | null {
    const value = this.inputs?.coinsurance
    return value != null ? Math.round(value * 10000) / 100 : null
  }

  set coinsurancePercent(percent: number | null) {
    if (typeof percent === 'number') {
      if (percent > 100) {
        percent = 100
      }
      this.inputs.coinsurance = percent / 100
    } else {
      this.inputs.coinsurance = null
    }
    this.estimateService.markDirty()
  }

  get secondaryCoinsurancePercent(): number | null {
    const value = this.inputs?.secondaryCoinsurance
    return value != null ? Math.round(value * 10000) / 100 : null
  }

  set secondaryCoinsurancePercent(percent: number | null) {
    if (typeof percent === 'number') {
      if (percent > 100) {
        percent = 100
      }
      this.inputs.secondaryCoinsurance = percent / 100
    } else {
      this.inputs.secondaryCoinsurance = null
    }
    this.estimateService.markDirty()
  }

  resetToOriginal(field: BenefitField) {
    if (!this.inputs) return

    const source = this.isSecondaryField(field) ? this.originalSecondaryBenefits : this.originalBenefits
    if (!source) return

    const key = this.toPrimaryKey(field)
    const defaultValue = key === PrimaryBenefitField.OutOfPocketMax ? null : 0
    const originalValue = source[key] ?? defaultValue

    this.inputs[field] = originalValue
    this.estimateService.markDirty()
    this.estimateService.calcAndSaveDisabled.next(false)
    this.estimateService.needsPricing$.next(true)
  }

  onOutOutPocketNilChange(event: MatCheckboxChange) {
    this.inputs.outOfPocketMax = event.checked ? null : 0
    this.estimateService.markDirty()
  }

  onSecondaryOutOfPocketNilChange(event: MatCheckboxChange) {
    this.inputs.secondaryOutOfPocketMax = event.checked ? null : 0
    this.estimateService.markDirty()
  }

  onPopoverKeyup(event: KeyboardEvent, popup: any, closeOnEnter = true) {
    if ((closeOnEnter && event.key === 'Enter') || event.key === 'Escape') {
      event.stopPropagation()
      popup.close()
      ;(event.target as any).blur()
    }
  }

  secondaryCoverageSummary() {
    const covers: string[] = []
    const notCovered: string[] = []

    if (this.inputs.secondaryInsuranceCoversDeductible) {
      covers.push('deductible')
    } else {
      notCovered.push('deductible')
    }
    if (this.inputs.secondaryInsuranceCoversCopayment) {
      covers.push('copayment')
    } else {
      notCovered.push('copayment')
    }
    if (this.inputs.secondaryInsuranceCoversCoinsurance) {
      covers.push('coinsurance')
    } else {
      notCovered.push('coinsurance')
    }

    if (covers.length > 0 && notCovered.length > 0) {
      return `Covers ${covers.join(
        ', ',
      )}.<br><strong>Does not cover ${notCovered.join(', ')}.</strong>`
    } else if (covers.length === 3) {
      return `Covers deductible, copayment, and coinsurance.`
    } else if (covers.length === 2) {
      return `Covers ${covers[0]} and ${covers[1]}.`
    } else if (covers.length > 0) {
      return `Covers ${covers.join(', ')}.`
    } else {
      return `N/A`
    }
  }

  valueChanged() {
    this.estimateService.calcAndSaveDisabled.next(false)
    this.estimateService.markDirty()
  }

  compareById(first: PatientInsurance | null, second: PatientInsurance | null): boolean {
    return first?.id === second?.id
  }

  selectPrimaryInsurance(insurance: PatientInsurance) {
    this.estimateService.setPrimaryInsurance(insurance)
  }

  selectSecondaryInsurance(insurance: PatientInsurance) {
    this.estimateService.setSecondaryInsurance(insurance)
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

}
